import { atomWithKey as atom } from "./atomWithKey";
import { atom as jotaiAtom } from "jotai";
import { IBookingRequest, IBookingResponse, IPassBooking } from "../Types/Types/BookingTypes";
import { paymentInboundAtom } from "./OfferAtoms";
import { paymentOutboundAtom, selectedOutboundTrainAtom, selectedTrainStateAtom } from "./OfferAtoms";
import { IPendingVouchers } from "../Types/Interface/IVoucher";
import { listOfAppliedVoucherAtom } from "./VoucherAtom";
import { trainShuttleBookingAtom } from "./ShuttleAtom";
import { totalPassengersSelectorAtom } from "./PassengerAtoms";
import { IBookedJourneyDetail, IBookedTrips, IBooking, IBookingComment, IBookingCommentResponse, IBookingSearchResult, IMultiTripsBookingSummary, IPaymentResponse } from "../Types/Index";
import { TrainType } from "../Types/Types/TrainTypes";
import { IRetrieveForm } from "../Types/Interface/IBookingTrip";
import { IMealPreference } from "../Types/Interface/IMealPreference";
import { ITicketOption } from "../Types/Interface/ITicketOption";
import { IAutoSuggestionList } from "../Types/Interface/ActionParams/RetrieveBookingParams";
import { SHUTTLE_PRODUCT_CODE, TripTypeEnum } from "../Types/Enums/globalDefaultEnum";
import { ISeatingPreference } from "../Types/Interface/ISeatingPreference";
import { checkoutPaymentSummaryAtom, ICheckoutPaymentSummary } from "./PaymentAtom";

export const searchPopupDataAtom = atom<any>({
    key: "searchPopupDataAtom",
    default: { search: {}, recoilState: {}, oldSearch: null },
});

export const bookingRequestAtom = atom<IBookingRequest | null>({
    key: "bookingRequestAtom",
    default: null,
});

export const bookingResponseAtom = atom<IBookingResponse | null>({
    key: "bookingResponseAtom",
    default: null,
});
/**
 * Bump this to make the booking-details page re-run its own data fetch.
 *
 * The page renders from three stores — `bookingResponseAtom` (trip cards),
 * `checkoutPaymentSummaryAtom` (Booking summary) and `bookingPaymentDataAtom`
 * (refunded amount) — and the last two are written ONLY by
 * GetBookingDetailDataAction, which fires from the page's mount effect. An
 * aftersales change made without leaving the page (onward-travel refund) can
 * refresh the booking itself but has no way to rebuild the other two, so the
 * page kept showing pre-refund figures until a manual browser refresh.
 *
 * Deliberately a token rather than a direct re-run: `direction` lives in the
 * URL, not in an atom, so only the page can reproduce the exact params — and
 * its fetch re-applies the direction/passenger scoping that a raw booking write
 * drops. Identity only; any new value is a request.
 */
/**
 * The offers `bookingResponseAtom` has been NARROWED to, and the booking that
 * narrowing belongs to.
 *
 * Add-return-trip scopes the atom to the new offers (filterBookingByOfferId) so
 * its checkout prices only what is unpaid. Any later full re-read of the
 * booking — every E2E add ends with one — silently widened it back, and the
 * already-paid outbound reappeared in the summary AND in the total: a 64 AED
 * checkout asked for 124.
 *
 * `bookingId` is carried so a stale scope cannot bleed onto a different
 * booking: storeBookingResponseAtom re-applies it only on the booking it was
 * taken from, and drops it otherwise. Deliberately NOT whitelisted for
 * persistence — a scope must not survive a reload.
 */
export const bookingScopeAtom = atom<{ bookingId: string; offerIds: string[] } | null>({
    key: "bookingScopeAtom",
    default: null,
});

export const bookingDetailsRefreshTokenAtom = atom<string | null>({
    key: "bookingDetailsRefreshTokenAtom",
    default: null,
});
export const bookingResponseListAtom = atom<{ [key: string]: IBookingResponse }>({
    key: "bookingResponseListAtom",
    default: {},
});
export const quickExchangeBookingResponseAtom = atom<IBookingResponse | null>({
    key: "quickExchangeBookingResponseAtom",
    default: null,
});

export const outboundProductCodeAtom = atom<string | null>({
    key: "outboundProductCodeAtom",
    default: null,
});

export const inboundProductCodeAtom = atom<string | null>({
    key: "inboundProductCodeAtom",
    default: null,
});

export const getFinalBookingAmountSelector = jotaiAtom((get): {
    total: number;
    subTotal: number;
    totalAmountFulfilled: number;
    totalAmountFulfilledInBound: number;
    totalMealPrice: number;
    totalAmountRefunded: number;
    totalAmountRefundedInBound: number;
    totalVoucherAppliedPrice: number;
    alreadyPaidAmount: number;
    totalExchangeFee: number;
} => {

        const checkoutPaymentSummary = get(checkoutPaymentSummaryAtom) as ICheckoutPaymentSummary;
        // Calculate for normal flow
        const paymentItemsFulfilled = get(paymentOutboundAtom);
        const trainShuttleBooking = get<boolean>(trainShuttleBookingAtom);
        // const isBookReturnJourney = get<boolean>(isBookReturnJourneyAtom);
        const isBookReturnJourney = false;
        const totalPassengers = get(totalPassengersSelectorAtom);

        const paymentItemsFulfilledInBound = get(paymentInboundAtom);
        const listOfAppliedVoucher = get<IPendingVouchers[]>(listOfAppliedVoucherAtom);
        const outboundTrain: TrainType | null = get<TrainType | null>(selectedOutboundTrainAtom);
        const totalVoucherAppliedPrice = (listOfAppliedVoucher || []).reduce(
            (sum: number, obj: IPendingVouchers) => sum + obj.amount.amount,
            0
        );
        const totalAmountFulfilled = !isBookReturnJourney ? paymentItemsFulfilled.reduce((sum, obj) => sum + obj.amount, 0) : 0;
        const totalAmountFulfilledInBound = paymentItemsFulfilledInBound.reduce((sum, obj) => sum + obj.amount, 0);
        //const totalAmountFulfilledInBound = 0
        // const totalMealPrice = (
        //     Object.values(meal_).flat() as TransformedMeal[]
        // ).reduce((total: number, item: TransformedMeal) => total + item.price * item.count, 0);        

        const totalMealPrice = 0
        // const totalWithShuttle = 0;//(trainShuttleBooking ? ((totalPassengers.totalWithoutInfants) * Number((outboundTrain?.priceInfo?.shuttle?.price || 0) || (outboundTrain?.originShuttle?.priceInfo || 0))) : 0)
        // // + (outboundTrain?.originShuttle?.id ? ((totalPassengers.totalWithoutInfants || 0) * Number(outboundTrain?.originShuttle?.priceInfo)) : 0)
        // // +(outboundTrain?.destinationShuttle?.id ? ((totalPassengers.totalWithoutInfants || 0) * Number(outboundTrain?.destinationShuttle?.priceInfo)) : 0)

        let totalBilling: number = (
            totalAmountFulfilled +
            totalAmountFulfilledInBound +
            totalMealPrice +
            - totalVoucherAppliedPrice
        );
        /**
         * @flow This is for Train + Shuttle flow.
         * If train have shuttle then add the shuttle price to total billing.
         * This is required because total billing is calculated based on train price only.
         * So we need to add the shuttle price to total billing.
         * It's run only if @var trainShuttleBooking is false.
        */

        if ((outboundTrain?.legs || []).length > 0 && !trainShuttleBooking) {// && findShuttle.length !== legCodes.length) {
            const legCodes = outboundTrain?.legs?.map(leg => leg.legOffer?.[0]?.products?.[0]?.code).filter(Boolean) || [];
            const findShuttle = (legCodes || []).filter(code => code?.includes(SHUTTLE_PRODUCT_CODE));
            if (legCodes.length !== findShuttle.length) {
                const outboundTrainShuttle = outboundTrain?.legs?.filter(leg => leg.timedLeg.service.mode.ptMode === "BUS") || []
                if (outboundTrainShuttle?.length > 0) {
                    totalBilling = totalBilling + (totalPassengers.totalWithoutInfants * Number(outboundTrain?.priceInfo.shuttle?.price || 0))
                }
            }
        }
        if (trainShuttleBooking) {
            //Direct shuttle
            const totalWithShuttle = (trainShuttleBooking ? ((totalPassengers.totalWithoutInfants) * Number((outboundTrain?.priceInfo?.shuttle?.price || 0) || (outboundTrain?.originShuttle?.priceInfo || 0))) : 0)
                + (outboundTrain?.originShuttle?.id ? ((totalPassengers.totalWithoutInfants || 0) * Number(outboundTrain?.originShuttle?.priceInfo)) : 0)
                + (outboundTrain?.destinationShuttle?.id ? ((totalPassengers.totalWithoutInfants || 0) * Number(outboundTrain?.destinationShuttle?.priceInfo)) : 0)
            totalBilling += totalWithShuttle
        }

        if (checkoutPaymentSummary?.otherDetails?.offerPass) {
            totalBilling = totalBilling + checkoutPaymentSummary.otherDetails?.offerPass?.amount
        }

        // const paymentItemsRefunded = get<PaymentItemDetails[]>(paymentOutBoundRefundItemsDetails);
        // const paymentItemsRefundedInBound = get<PaymentItemDetails[]>(paymentOutBoundRefundItemsDetailsInBoundAtom);
        // const totalAmountRefunded = paymentItemsRefunded.reduce((sum, obj) => sum + obj.amount, 0);
        const totalAmountRefunded = 0;
        // const totalAmountRefundedInBound = paymentItemsRefundedInBound.reduce((sum, obj) => sum + obj.amount, 0);
        const totalAmountRefundedInBound = 0;
        let subTotal = totalAmountRefunded + totalAmountRefundedInBound + totalVoucherAppliedPrice


        // Calculating for add shuttle flow
        // const isAddShuttle = get<boolean>(isAddShuttleAtom);
        // const isAddShuttle = false
        // if (isAddShuttle) {
        //     const selectedPassengerForCancel = get<selectedPassengerForCancelIds[]>(selectedPassengerForCancelAtom);
        //     const selectedPassengerForCancelInBound = get<selectedPassengerForCancelIds[]>(selectedPassengerForCancelInBoundAtom);
        //     const outboundShuttleStates = get<shuttleStateType>(outboundShuttleState);
        //     const inboundShuttleStates = get<shuttleStateType>(inboundShuttleState);
        //     totalBilling = parseFloat(
        //         (
        //             selectedPassengerForCancel?.length *
        //             (outboundShuttleStates?.originOptions?.find((option: any) => option.label === outboundShuttleStates?.originFrom)?.amount || 0) +
        //             selectedPassengerForCancel?.length *
        //             (outboundShuttleStates?.destinationOptions?.find((option: any) => option.label === outboundShuttleStates?.destinationTo)?.amount || 0) +
        //             selectedPassengerForCancelInBound?.length *
        //             (inboundShuttleStates?.originOptions?.find((option: any) => option.label === inboundShuttleStates?.originFrom)?.amount || 0) +
        //             selectedPassengerForCancelInBound?.length *
        //             (inboundShuttleStates?.destinationOptions?.find((option: any) => option.label === inboundShuttleStates?.destinationTo)?.amount || 0)
        //         ).toFixed(2)
        //     )
        // }


        // const isCheckoutFromExchange = get<boolean>(isCheckoutFromExchangeAtom);
        let alreadyPaidAmount = 0;
        let totalExchangeFee = 0;
        // // If exchange checkout flow
        // if (isCheckoutFromExchange) {
        //     const exchangeOperationResponse = get<ExchangeOperation | null>(exchangeOperationResponseAtom);
        //     totalExchangeFee = exchangeOperationResponse?.exchangeOffers[0]?.exchangeFee ? calculatePrice(exchangeOperationResponse?.exchangeOffers[0]?.exchangeFee) : 0;
        //     const alreadyPaidOutBound = get<number>(alreadyPaidOutBoundAtom);
        //     const alreadyPaidInBound = get<number>(alreadyPaidInBoundAtom);
        //     alreadyPaidAmount = alreadyPaidOutBound + alreadyPaidInBound;

        //     const totalAmountFulfilled = paymentItemsFulfilled.reduce((sum, obj) => sum + obj.amount, 0);
        //     const totalAmountFulfilledInBound = paymentItemsFulfilledInBound?.reduce((sum, obj) => sum + obj.amount, 0);
        //     const totalMealPrice = (Object.values(meal_).flat() as TransformedMeal[]).reduce((total: number, item: TransformedMeal) => total + item.price * item.count, 0);
        //     totalBilling = (totalAmountFulfilled + totalAmountFulfilledInBound + totalMealPrice + totalExchangeFee) -
        //         (alreadyPaidAmount)
        //         - totalVoucherAppliedPrice
        //     subTotal = totalAmountFulfilled + (totalAmountFulfilledInBound || 0) + totalMealPrice + totalExchangeFee
        // }


        return {
            total: totalBilling,
            subTotal,
            totalAmountFulfilled,
            totalAmountFulfilledInBound,
            totalMealPrice,
            totalAmountRefunded,
            totalAmountRefundedInBound,
            totalVoucherAppliedPrice,
            alreadyPaidAmount,
            totalExchangeFee
        };
});
getFinalBookingAmountSelector.debugLabel = "getFinalBookingAmountSelector";

export const bookedTripsAtom = atom<IBookedTrips | null>({
    key: "bookedTripsAtom",
    default: null,
});

export interface IBookingsListPagination {
    activeTab: "1" | "2" | null;
    // Both tabs paginate lazily: how many summaries from `allResults` have had
    // their details built so far. "Load more" builds the next `pageSize`
    // summaries' details and appends them.
    offset: number;
    pageSize: number;
    // The full result set (summaries) for the active window/tab, sorted into
    // display (date) order. Details are built a page at a time from this so we
    // don't fetch every booking's detail up front.
    allResults: IBookingSearchResult[];
    // True while the next page's details are being built (async).
    isLoadingMore: boolean;
    // Filters captured on load (kept for parity with the tab/month-filter
    // actions that populate this atom).
    lastSearchRequest: any | null;
    lastIsAnonymous: boolean;
    lastRetrieveBookingsIds: string[];
}

export const bookingsListPaginationAtom = atom<IBookingsListPagination>({
    key: "bookingsListPaginationAtom",
    default: {
        activeTab: null,
        offset: 0,
        pageSize: 5,
        allResults: [],
        isLoadingMore: false,
        lastSearchRequest: null,
        lastIsAnonymous: false,
        lastRetrieveBookingsIds: [],
    },
});

export const upcomingMultiBookingTripsAtom = atom<IMultiTripsBookingSummary[]>({
    key: "upcomingMultiBookingTripsAtom",
    default: [],
});

export const pastMultiBookingTripsAtom = atom<IMultiTripsBookingSummary[]>({
    key: "pastMultiBookingTripsAtom",
    default: [],
});

export const isModalVisibleAtom = atom<boolean>({
    key: "isModalVisibleAtom",
    default: false,
});
export const isRoundTripModalVisibleAtom = atom<boolean>({
    key: "isRoundTripModalVisibleAtom",
    default: false,
});
export const isRoundTripInboundModalVisibleAtom = atom<boolean>({
    key: "isRoundTripInboundModalVisibleAtom",
    default: false,
})

export const bookedNextTripsAtom = atom<IBookedTrips | null>({
    key: "bookedNextTripsAtom",
    default: null,
});

export const activeBookingTabAtom = atom<string>({
    key: "activeBookingTabAtom",
    default: "1"
});

export const inboundSelectedDateAtom = atom<{
    [key: number]: string | null;
}>({
    key: "inboundSelectedDateAtom",
    default: "",
});

export const inboundSelectedChangeDateAtom = atom<{
    [key: number]: string | null;
}>({
    key: "inboundSelectedChangeDateAtom",
    default: "",
});

export const selectedChangeDateAtom = atom<{
    [key: number]: string | null;
}>({
    key: "selectedChangeDateAtom",
    default: "",
});

export const selectedBookReturnJourneyDateAtom = atom<{
    [key: number]: string | null;
}>({
    key: "selectedBookReturnJourneyDateAtom",
    default: "",
});

export const SelectedOfferIdChangeDateAtom = atom<string>({
    key: "SelectedOfferIdChangeDateAtom",
    default: "",
});


export const selectedInboundTrainStateAtom = atom<TrainType | null>({
    key: "selectedInboundTrainStateAtom",
    default: null,
});

export const lastMonthAtom = atom<string>({
    key: "lastMonthAtom",
    default: "3",
});

export const retrieveFormAtom = atom<IRetrieveForm>({
    key: "retrieveFormAtom",
    default: {
        value: {
            bookingReference: "",
            lastName: "",
        },
        error: {
            bookingReference: "",
            lastName: "",
        },
    },
});

export const isPastBookingDetailsAtom = atom<boolean>({
    key: "isPastBookingDetailsAtom",
    default: false,
});

export const bookingConfirmStatusAtom = atom<string>({
    key: "bookingConfirmStatusAtom",
    default: "",
});

export const cancelBookingConfirmPopupFlagAtom = atom<boolean>({
    key: "cancelBookingConfirmPopupFlagAtom",
    default: false
});

export const paymentItemsState = jotaiAtom((get) => {
        const currentTrain = get(selectedTrainStateAtom);
        const { passengerCounts } = get(totalPassengersSelectorAtom);
        const discountedPrice = 0;
        // Generate passenger items based on passengerCounts
        const passengerItems = passengerCounts
            ? Object.entries(passengerCounts).map(([type, count]) => ({
                description: `${count} ${type.charAt(0).toUpperCase() + type.slice(1)
                    }`,
                amount: `AED ${count * discountedPrice}`,
                passengerCnt: count,
                passengerType: type,
            }))
            : [];

        const items: Array<{
            description: string;
            amount: string;
            passengerCnt?: number;
            passengerType?: string;
        }> = [...passengerItems];
        return items;
});
paymentItemsState.debugLabel = "paymentItemsState";

export const repeatedBookingAtom = atom<any>({
    key: "repeatedBookingAtom",
    default: { tripStatus: "", repeatBooking: "", direction: "" },
});

export const isShuttleChangeDateAtom = atom<boolean>({
    key: "isShuttleChangeDateAtom",
    default: false,
});

export const displayPassengerDataAtom = atom<any>({
    key: "displayPassengerDataAtom",
    default: null,
});

export const mealResponseAtom = atom<IMealPreference[]>({
    key: "mealResponseAtom",
    default: [],
});

export const ticketOptionsAtom = atom<ITicketOption[]>({
    key: "ticketOptionsAtom",
    default: []
});
export const seatingPreferencesAtom = atom<ISeatingPreference | null>({
    key: "seatingPreferencesAtom",
    default: null
});
export const isRetrieveModalOpenAtom = atom<boolean>({
    key: "isRetrieveModalOpenAtom",
    default: false,
});

export const selectedTripsDataAtom = atom<any>({
    key: "selectedTripsDataAtom",
    default: null,
})

export const showRetrieveBookingAtom = atom<boolean>({
    key: "showRetrieveBookingAtom",
    default: false,
});

export const autoSuggestionListAtom = atom<IAutoSuggestionList>({
    key: "autoSuggestionListAtom",
    default: {
        bookingReferenceList: [],
        lastNameList: []
    },
});

export const pendingBookingDataAtom = atom<any>({
    key: "pendingBookingDataAtom",
    default: null
});

// Per-trip modification-blocked map: bookingId -> tripId -> true when the
// exchange-offers prefetch for that specific trip responded with
// `BOOKING_MODIFICATION_NOT_ALLOWED`. Consumed by BookingOptions to hide
// "Change date or time" only when *every* trip in the booking is blocked
// — previously the gate was per-booking, which produced a false positive
// on mixed-tier round-trips (e.g. outbound Saver + inbound Value would
// hide the menu for both legs even though Value is modifiable).
//
// Trip-level granularity also lets future per-trip UI gate accurately
// (e.g. quick-exchange per-leg buttons). Not whitelisted — recomputed
// each session. Absent entry = not yet probed (treated as "allowed").
export const modificationNotAllowedBookingIdsAtom = atom<Record<string, Record<string, boolean>>>({
    key: "modificationNotAllowedBookingIdsAtom",
    default: {},
});

export const guestCheckoutBookingIdAtom = atom<string>({
    key: "guestCheckoutBookingIdAtom",
    default: ""
});
export const selectedBookingForBookItAgainAtom = atom<IBooking | null>({
    key: "selectedBookingForBookItAgainAtom",
    default: null
});

export const originalPriceOfExchangedItemsInboundAtom = atom<any>({
    key: "originalPriceOfExchangedItemsInboundAtom",
    default: null
});

export const originalPriceOfExchangedItemsOutboundAtom = atom<any>({
    key: "originalPriceOfExchangedItemsOutboundAtom",
    default: null
});

// Refund offer related atoms
export const refundOfferResponseAtom = atom<any>({
    key: "refundOfferResponseAtom",
    default: null
});

export const refundFeeAtom = atom<any>({
    key: "refundFeeAtom",
    default: null
});

export const refundableAmountAtom = atom<any>({
    key: "refundableAmountAtom",
    default: null
});

/**
 * The last refund quote could not be priced.
 *
 * Money code: `refundFeeAtom === null` means "not quoted yet" (the receipt shows
 * a skeleton), and that is NOT the same as "the retailer declined to quote" — a
 * skeleton that never resolves reads as a slow network and invites the passenger
 * to confirm a cancellation whose fee nobody knows. This flag is what turns the
 * second case into a visible error instead.
 *
 * Deliberately NOT persisted (absent from WhitelistedAtoms): a stale failure
 * surviving into an unrelated booking would block a cancellation that is fine.
 */
export const refundQuoteFailedAtom = atom<boolean>({
    key: "refundQuoteFailedAtom",
    default: false
});

export const defaultActiveKeyForTripSummaryAtom = atom<any>({
    key: "defaultActiveKeyForTripSummaryAtom",
    default: []
});

export const bookedCommentsResponseAtom = atom<IBookingCommentResponse | null>({
    key: "bookedCommentsResponseAtom",
    default: null
})

export const bookedJourneyCommentAtom = atom<IBookingComment | null>({
    key: "bookedJourneyCommentAtom",
    default: null
});

export const journeyPlannerStateAtom = atom<IBookedJourneyDetail | null>({
    key: "journeyPlannerStateAtom",
    default: null
});

export const journeyPlannerTrainTypeTabAtom = atom<TripTypeEnum>({
    key: "journeyPlannerTrainTypeTabAtom",
    default: TripTypeEnum.OUT_BOUND
});

export const bookingPaymentDataAtom = atom<IPaymentResponse[]>({
    key: "bookingPaymentDataAtom",
    default: []
});

export interface IAppliedPassengerPassAtomType {
    outbound?: {
        externalRef: string;
        passOffer: IPassBooking
    }[],
    inbound?: {
        externalRef: string;
        passOffer: IPassBooking
    }[]
}

export const appliedPassengerPassAtom = atom<IAppliedPassengerPassAtomType | null>({
    key: "appliedPassengerPassAtom",
    default: null
});
